import { usersGraphqlService } from '@/services/api';
import { PatientEnroller } from '@/hooks/pa/usePatientEnrollers.ts';

export interface DashboardStats {
  total: number;
  active: number;
  deferred: number;
  followUp: number;
}

export interface TaskStatus {
  type: 'deferred_followup' | 'deferred' | 'followup' | 'none';
  label: string;
  className: string;
}

export interface ClinicNamesFormat {
  visibleClinics: string[];
  remainingCount: number;
  hasMore: boolean;
}

export interface PaginationResult<T> {
  currentRecords: T[];
  totalPages: number;
  startIndex: number;
  endIndex: number;
  totalRecords: number;
}

export class PESAdminService {
  /**
   * Fetch all patient enrollment specialists using centralized GraphQL client
   */
  async fetchPatientEnrollers(hasuraToken: string): Promise<PatientEnroller[]> {
    try {
      const result = await usersGraphqlService.getPatientEnrollers(hasuraToken);
      return (result.data?.pep_vw_patient_enroller_list || []) as unknown as PatientEnroller[];
    } catch (error) {
      console.error('Error fetching patient enrollers:', error);
      throw new Error('Failed to fetch patient enrollers');
    }
  }

  /**
   * Calculate dashboard statistics from patient enrollers data
   */
  calculateDashboardStats(patientEnrollers: PatientEnroller[]): DashboardStats {
    return {
      total: patientEnrollers.length,
      active: patientEnrollers.filter(pe => !pe.deferred_task).length,
      deferred: patientEnrollers.filter(pe => pe.deferred_task).length,
      followUp: patientEnrollers.filter(pe => pe.follow_up).length,
    };
  }

  /**
   * Format clinic names for display with "+X more" logic
   */
  formatClinicNames(clinicNames: string, maxVisible: number = 4): ClinicNamesFormat {
    if (!clinicNames || clinicNames === 'No Clinics Assigned') {
      return {
        visibleClinics: [],
        remainingCount: 0,
        hasMore: false
      };
    }

    const clinics = clinicNames.split(', ').map(name => name.trim());

    if (clinics.length <= maxVisible) {
      return {
        visibleClinics: clinics,
        remainingCount: 0,
        hasMore: false
      };
    }

    return {
      visibleClinics: clinics.slice(0, maxVisible),
      remainingCount: clinics.length - maxVisible,
      hasMore: true
    };
  }

  /**
   * Render clinic names with proper Badge components (returns JSX structure info)
   */
  renderClinicNames(clinicNames: string, maxVisible: number = 4) {
    const formatted = this.formatClinicNames(clinicNames, maxVisible);
    return {
      ...formatted,
      isEmpty: !clinicNames || clinicNames === 'No Clinics Assigned'
    };
  }

  /**
   * Get task status information for a patient enroller
   */
  getTaskStatus(pe: PatientEnroller): TaskStatus {
    if (pe.deferred_task && pe.follow_up) {
      return {
        type: 'deferred_followup',
        label: 'Deferred, Follow up',
        className: 'bg-yellow-100 text-yellow-800'
      };
    } else if (pe.deferred_task) {
      return {
        type: 'deferred',
        label: 'Deferred',
        className: 'bg-orange-100 text-orange-800'
      };
    } else if (pe.follow_up) {
      return {
        type: 'followup',
        label: 'Follow up',
        className: 'bg-blue-100 text-blue-800'
      };
    }

    return {
      type: 'none',
      label: 'No Task Assigned',
      className: 'bg-green-100 text-green-800'
    };
  }

  /**
   * Paginate patient enrollers data
   */
  paginateData<T>(
    data: T[],
    currentPage: number,
    recordsPerPage: number
  ): PaginationResult<T> {
    const totalRecords = data.length;
    const totalPages = Math.ceil(totalRecords / recordsPerPage);
    const startIndex = (currentPage - 1) * recordsPerPage;
    const endIndex = startIndex + recordsPerPage;
    const currentRecords = data.slice(startIndex, endIndex);

    return {
      currentRecords,
      totalPages,
      startIndex,
      endIndex,
      totalRecords
    };
  }

}

export const pesAdminService = new PESAdminService();